import { ACTIVE_PROFILE_STORAGE_KEY, BUSINESS_TYPE } from '@/constants';
import type { BusinessType, PersistedActiveProfile } from '@/types';
import { PL_LOG_GROUPS, logger } from '@/utils';
import { LOG_LEVEL, getComponentRegistryConfig, getQueryParameter, storage } from '@b2x/shared';

export async function resolveBusinessType(): Promise<BusinessType> {
  // Enforced by EMH components
  const emhBusinessType = await getBusinessTypeFromEMH();
  if (emhBusinessType) {
    logger('Resolved business type from EMH config:', {
      args: [emhBusinessType],
      group: PL_LOG_GROUPS.CORE,
      level: LOG_LEVEL.INFO,
    });
    return emhBusinessType;
  }

  // Inferred from URL path
  const urlBusinessType = getBusinessTypeFromURL();
  if (urlBusinessType) {
    logger('Resolved business type from URL path:', {
      args: [urlBusinessType],
      group: PL_LOG_GROUPS.CORE,
      level: LOG_LEVEL.INFO,
    });
    return urlBusinessType;
  }

  // Enforced by query parameter (ctx)
  const queryParamBusinessType = getBusinessTypeFromQueryParam();
  if (queryParamBusinessType) {
    logger('Resolved business type from query parameter:', {
      args: [queryParamBusinessType],
      group: PL_LOG_GROUPS.CORE,
      level: LOG_LEVEL.INFO,
    });
    return queryParamBusinessType;
  }

  // Fallback from local storage from last active profile
  const localStorageContext = getBusinessTypeFromLocalStorage();
  if (localStorageContext) {
    logger('Resolved business type from local storage from the last active profile:', {
      args: [localStorageContext],
      group: PL_LOG_GROUPS.CORE,
      level: LOG_LEVEL.INFO,
    });
    return localStorageContext;
  }

  // Fallback to default (B2C)
  const fallbackBusinessType = BUSINESS_TYPE.B2C;
  logger('Resolved business type falling back to default business type (B2C):', {
    args: [fallbackBusinessType],
    group: PL_LOG_GROUPS.CORE,
    level: LOG_LEVEL.INFO,
  });
  return fallbackBusinessType;
}

async function getBusinessTypeFromEMH(): Promise<BusinessType | null> {
  const [emhContextBarConfig] = await getComponentRegistryConfig((components) => {
    return [components.find((component) => component.payload?.userContextType)] as any[];
  });

  const forcedBusinessType = emhContextBarConfig?.payload?.forceUserContextType;
  const businessType = emhContextBarConfig?.payload?.userContextType;

  return forcedBusinessType ? (businessType?.toUpperCase() as BusinessType) : null;
}

function getBusinessTypeFromURL(): BusinessType | null {
  const isB2cPage = location.href.includes('/my-area');
  const isB2bPage = location.href.includes('/my-area/business');
  const hasLandedDirectlyOnVansPage = document.referrer === '' && location.href.includes('/vans');

  const businessTypeAgnosticPages = ['/profile-overview', '/my-wishlist', '/logout-successful'];

  if (businessTypeAgnosticPages.some((url) => location.href.includes(url))) {
    return null;
  }

  if (isB2bPage || hasLandedDirectlyOnVansPage) {
    return BUSINESS_TYPE.B2B;
  }
  if (isB2cPage) {
    return BUSINESS_TYPE.B2C;
  }

  return null;
}

function getBusinessTypeFromQueryParam(): BusinessType | null {
  const businessType = getQueryParameter('ctx')?.toUpperCase();
  if (businessType && isValidBusinessType(businessType)) {
    return businessType;
  }
  return null;
}

function getBusinessTypeFromLocalStorage(): BusinessType | null {
  const lastActiveProfile = storage.local.get<PersistedActiveProfile>(ACTIVE_PROFILE_STORAGE_KEY);

  if (lastActiveProfile?.businessType) {
    return lastActiveProfile.businessType;
  }

  return null;
}

function isValidBusinessType(value: string): value is BusinessType {
  return Object.values(BUSINESS_TYPE).includes(value as BusinessType);
}
